import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ClipboardList, AlertTriangle, CheckCircle2 } from "lucide-react";

function Stat({ label, value, cls }) {
  return (
    <Card className="p-4 rounded-md flex-1 min-w-[120px]">
      <div className={`text-2xl font-bold tabular ${cls || "text-cass-navy"}`}>{value}</div>
      <div className="text-xs text-cass-slate mt-0.5">{label}</div>
    </Card>
  );
}

function TaskRow({ t, onToggle }) {
  return (
    <div className={`flex items-start gap-3 border-b border-border py-3 ${t.completed ? "opacity-60" : ""}`} data-testid={`my-task-${t.task_id}`}>
      <Checkbox checked={t.completed} onCheckedChange={(v) => onToggle(t, !!v)} className="mt-0.5" data-testid={`my-task-check-${t.task_id}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${t.completed ? "line-through text-cass-slate" : "text-cass-navy"}`}>{t.title}</div>
        <div className="text-xs text-cass-slate mt-0.5 truncate">
          {t.client_name} · {t.proposal_ref} · {t.service_name}
        </div>
      </div>
      {t.due_date && (
        <Badge variant={t.overdue ? "destructive" : "outline"} className="shrink-0" data-testid={`my-task-due-${t.task_id}`}>
          {t.overdue && <AlertTriangle className="w-3 h-3 mr-1" />}
          due {new Date(t.due_date).toLocaleDateString("en-GB")}
        </Badge>
      )}
    </div>
  );
}

export default function MyTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/engager/my-tasks").then((r) => setTasks(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggle = async (t, completed) => {
    setTasks((ts) => ts.map((x) => (x.task_id === t.task_id && x.setup_id === t.setup_id ? { ...x, completed, overdue: completed ? false : x.overdue } : x)));
    try { await api.post(`/engager-setups/${t.setup_id}/toggle`, { task_id: t.task_id, completed }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); load(); }
  };

  const open = tasks.filter((t) => !t.completed);
  const done = tasks.filter((t) => t.completed);
  const overdueCount = open.filter((t) => t.overdue).length;
  const todayIso = new Date().toISOString().slice(0, 10);
  const dueToday = open.filter((t) => t.due_date === todayIso).length;

  return (
    <div className="p-8 max-w-3xl mx-auto space-y-6" data-testid="my-tasks-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-cass-navy flex items-center gap-2"><ClipboardList className="w-7 h-7 text-cass-teal" />My Tasks</h1>
        <p className="text-cass-slate mt-1">Your onboarding tasks assigned across all clients.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Stat label="Open" value={open.length} />
        <Stat label="Overdue" value={overdueCount} cls={overdueCount ? "text-red-600" : "text-cass-navy"} />
        <Stat label="Due today" value={dueToday} cls={dueToday ? "text-amber-600" : "text-cass-navy"} />
        <Stat label="Completed" value={done.length} cls="text-emerald-600" />
      </div>

      {loading ? (
        <p className="text-cass-slate text-sm">Loading your tasks…</p>
      ) : tasks.length === 0 ? (
        <Card className="p-8 rounded-md text-center text-cass-slate" data-testid="my-tasks-empty">
          <CheckCircle2 className="w-8 h-8 text-cass-teal mx-auto mb-2" />
          You have no onboarding tasks assigned to you right now.
        </Card>
      ) : (
        <>
          <Card className="p-5 rounded-md">
            <div className="text-sm font-semibold text-cass-navy mb-1">To do <span className="text-cass-slate">({open.length})</span></div>
            {open.length === 0 && <p className="text-sm text-cass-slate italic py-2">All caught up 🎉</p>}
            {open.map((t) => <TaskRow key={`${t.setup_id}-${t.task_id}`} t={t} onToggle={toggle} />)}
          </Card>
          {done.length > 0 && (
            <Card className="p-5 rounded-md">
              <div className="text-sm font-semibold text-cass-navy mb-1">Completed <span className="text-cass-slate">({done.length})</span></div>
              {done.map((t) => <TaskRow key={`${t.setup_id}-${t.task_id}`} t={t} onToggle={toggle} />)}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
